/**
 * CostAnalysisPage — /cost
 *
 * US-24-GR-14 — Four sub-views: provider, user, outputType, time.
 * Sub-view persists in URL via ?view=provider|user|outputType|time.
 *
 * US-24-GR-07 — Manual Credit Ledger Adjustment
 * By User/Profile sub-tab: clicking a profile row opens a drawer with
 * "Adjust credits" action.
 */

import { useState, useEffect, useCallback, useRef, startTransition, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import {
  getCost,
  adjustCredits,
  InsufficientBalanceError,
  ProfileNotFoundError,
  CreditCeilingError,
  type CostGroupBy,
  type CostResponse,
  type CostRow,
} from '../api/orchestratorClient';
import {
  TimeWindowSelector,
  loadStoredWindow,
  saveStoredWindow,
  type TimeWindow,
} from '../components/TimeWindowSelector';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { Donut } from '../components/Donut';
import { BarChart } from '../components/BarChart';
import { LineChart } from '../components/LineChart';
import { AdjustCreditsModal } from '../components/AdjustCreditsModal';
import { Toast, type ToastState } from '../components/Toast';
import { AbbrLabel } from '../components/AbbrLabel';
import { downloadCsv, type CsvColumn } from '../utils/downloadCsv';

// ---------------------------------------------------------------------------
// Sub-view types
// ---------------------------------------------------------------------------

type SubView = 'provider' | 'user' | 'outputType' | 'time';

const SUBVIEW_LABELS: Record<SubView, string> = {
  provider:   'Provider / Model',
  user:       'User / Profile',
  outputType: 'Output Type',
  time:       'Burn Rate (Time)',
};

const SUBVIEW_GROUPBY: Record<SubView, CostGroupBy> = {
  provider:   'provider',
  user:       'user',
  outputType: 'outputType',
  time:       'time',
};

// ---------------------------------------------------------------------------
// Palette for donut/bar charts
// ---------------------------------------------------------------------------

const PALETTE = [
  '#7c3aed', '#10b981', '#f59e0b', '#ef4444',
  '#3b82f6', '#ec4899', '#14b8a6', '#f97316',
];

// ---------------------------------------------------------------------------
// CSV export (US-17-GR-29) — shared CostRow column defs across all sub-views
// ---------------------------------------------------------------------------

const COST_CSV_COLUMNS: CsvColumn<CostRow>[] = [
  { header: 'Key', accessor: (r) => r.key },
  { header: 'Runs', accessor: (r) => r.runs },
  { header: 'Input Tokens', accessor: (r) => r.inputTokens },
  { header: 'Output Tokens', accessor: (r) => r.outputTokens },
  { header: 'Estimated USD', accessor: (r) => r.estimatedUsd },
  { header: 'Credits Charged', accessor: (r) => r.creditsCharged },
  { header: 'Judge Tokens', accessor: (r) => r.judgeTokens ?? null },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtUsd(v: number): string {
  return `$${v.toFixed(4)}`;
}

function fmtCredits(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString();
}

/** Judge-token (quality gate) overhead formatter — '—' when not attributable. */
function fmtJudgeTokens(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString();
}

function truncateKey(key: string, maxLen = 20): string {
  return key.length > maxLen ? key.slice(0, maxLen - 1) + '…' : key;
}

/** Time-view bucket keys are `YYYY-MM-DD` — render a short `MM-DD` axis label. */
function formatShortDate(key: string): string {
  const match = /^\d{4}-(\d{2})-(\d{2})/.exec(key);
  if (match) return `${match[1]}-${match[2]}`;
  return truncateKey(key, 8);
}

// ---------------------------------------------------------------------------
// Provider sub-view
// ---------------------------------------------------------------------------

function ProviderView({ data }: { data: CostResponse }) {
  const slices = data.rows.map((row, i) => ({
    label: row.key,
    value: row.estimatedUsd,
    color: PALETTE[i % PALETTE.length],
  }));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-8">
        <Donut slices={slices} size={160} thickness={32} />
        <div className="flex flex-col gap-2">
          {slices.map((s) => (
            <span key={s.label} className="flex items-center gap-2 text-xs text-zinc-300">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
              {s.label}: {fmtUsd(s.value)}
            </span>
          ))}
        </div>
      </div>

      <SortableTable rows={data.rows} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// User sub-view — profile drawer + Adjust Credits
// ---------------------------------------------------------------------------

interface ProfileDrawerTarget {
  profileId: string;
  creditsCharged: number | null;
}

interface UserViewProps {
  data: CostResponse;
  onAdjustCredits: (target: ProfileDrawerTarget) => void;
  /** US-24-GR-26 item 5 — deep-link from /users?…→"View costs" preselects this profile. */
  deepLinkProfileId?: string | null;
}

function UserView({ data, onAdjustCredits, deepLinkProfileId }: UserViewProps) {
  const navigate = useNavigate();
  // Preselect + open the drawer for a deep-linked profileId as soon as this
  // view mounts with data (the parent only renders UserView once data has
  // loaded — see CostAnalysisPage's tabpanel gate below).
  const [selectedProfile, setSelectedProfile] = useState<ProfileDrawerTarget | null>(() => {
    if (!deepLinkProfileId) return null;
    const row = data.rows.find((r) => r.key === deepLinkProfileId);
    return row ? { profileId: row.key, creditsCharged: row.creditsCharged } : null;
  });
  const drawerRef = useRef<HTMLDivElement>(null);

  const top25 = [...data.rows]
    .sort((a, b) => b.estimatedUsd - a.estimatedUsd)
    .slice(0, 25);

  // Ensure the deep-linked profile is visible even if it falls outside the
  // top-25-by-spend cut — it's the whole reason this view was opened.
  const deepLinkRow = deepLinkProfileId
    ? data.rows.find((r) => r.key === deepLinkProfileId)
    : undefined;
  const displayRows =
    deepLinkRow && !top25.some((r) => r.key === deepLinkRow.key)
      ? [deepLinkRow, ...top25]
      : top25;

  // Close drawer on ESC
  useEffect(() => {
    if (!selectedProfile) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelectedProfile(null);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [selectedProfile]);

  // Restore focus when drawer closes
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  function handleRowClick(row: CostRow, btn: HTMLButtonElement) {
    triggerRef.current = btn;
    setSelectedProfile({ profileId: row.key, creditsCharged: row.creditsCharged });
  }
  function closeDrawer() {
    setSelectedProfile(null);
    setTimeout(() => triggerRef.current?.focus(), 0);
  }

  return (
    <div className="flex gap-4 min-h-0">
      {/* Top-spenders table */}
      <div className="flex-1 overflow-x-auto">
        <table className="w-full text-xs text-left border-collapse">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="px-3 py-2 text-zinc-400 font-medium">ProfileId</th>
              <th className="px-3 py-2 text-zinc-400 font-medium">Runs</th>
              <th className="px-3 py-2 text-zinc-400 font-medium">Credits</th>
              <th className="px-3 py-2 text-zinc-400 font-medium">
                <AbbrLabel term="Est. USD" />
              </th>
              <th className="px-3 py-2 text-zinc-400 font-medium" title="Quality gate judge tokens — platform overhead, not billed to users">
                Judge Tokens <span className="text-zinc-600">(overhead)</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => (
              <tr
                key={row.key}
                className={[
                  'border-b border-zinc-800/50 hover:bg-zinc-800/40',
                  selectedProfile?.profileId === row.key ? 'bg-zinc-800/60' : '',
                ].join(' ')}
              >
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={(e) => handleRowClick(row, e.currentTarget)}
                    className="font-mono text-violet-400 hover:text-violet-300 text-left"
                    title={row.key}
                    aria-pressed={selectedProfile?.profileId === row.key}
                    aria-label={`Open profile drawer for ${row.key}`}
                  >
                    {truncateKey(row.key)}
                  </button>
                </td>
                <td className="px-3 py-2 text-zinc-300">{row.runs.toLocaleString()}</td>
                <td className="px-3 py-2 text-zinc-300">{fmtCredits(row.creditsCharged)}</td>
                <td className="px-3 py-2 text-zinc-300">{fmtUsd(row.estimatedUsd)}</td>
                <td className="px-3 py-2 text-zinc-500">{fmtJudgeTokens(row.judgeTokens)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Profile detail drawer */}
      {selectedProfile && (
        <div
          ref={drawerRef}
          role="complementary"
          aria-label={`Profile detail — ${selectedProfile.profileId}`}
          className="w-72 flex-shrink-0 bg-zinc-900 border border-zinc-700 rounded-xl p-4 flex flex-col gap-4 self-start"
        >
          {/* Drawer header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-1 min-w-0">
              <p className="text-xs font-medium text-zinc-400">Profile</p>
              <p
                className="text-xs font-mono text-zinc-100 break-all"
                title={selectedProfile.profileId}
              >
                {selectedProfile.profileId}
              </p>
            </div>
            <button
              type="button"
              onClick={closeDrawer}
              aria-label="Close profile drawer"
              className="flex-shrink-0 text-zinc-500 hover:text-zinc-300 transition-colors mt-0.5"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Credits in window */}
          <div className="flex flex-col gap-1">
            <p className="text-xs text-zinc-400">Credits charged (this window)</p>
            <p className="text-lg font-bold text-zinc-100">
              {fmtCredits(selectedProfile.creditsCharged)}
            </p>
          </div>

          {/* Divider */}
          <hr className="border-zinc-800" />

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => onAdjustCredits(selectedProfile)}
              className="w-full min-h-[48px] px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-zinc-950 text-xs font-medium transition-colors text-left"
            >
              Adjust credits…
            </button>
            <button
              type="button"
              onClick={() =>
                navigate(`/jobs?profileId=${encodeURIComponent(selectedProfile.profileId)}`)
              }
              className="w-full min-h-[48px] px-3 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-xs font-medium hover:bg-zinc-800 hover:text-zinc-100 transition-colors text-left"
            >
              View job history →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Output type sub-view
// ---------------------------------------------------------------------------

function OutputTypeView({ data }: { data: CostResponse }) {
  const barRows = data.rows.map((row, i) => ({
    label: row.key,
    value: row.estimatedUsd,
    color: PALETTE[i % PALETTE.length],
  }));

  return (
    <div className="flex flex-col gap-6">
      <BarChart rows={barRows} maxWidth={500} valueFormatter={fmtUsd} />
      <SortableTable rows={data.rows} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time sub-view
// ---------------------------------------------------------------------------

function TimeView({ data }: { data: CostResponse }) {
  const points = data.rows.map((row) => ({
    label: formatShortDate(row.key),
    value: row.estimatedUsd,
  }));

  // WS-3 item 2.1: judge-token overhead per bucket — its own series, platform overhead
  const judgePoints = data.rows.map((row) => ({
    label: formatShortDate(row.key),
    value: row.judgeTokens ?? 0,
  }));
  const hasJudgeOverhead = (data.totalJudgeTokens ?? 0) > 0;

  const total = data.totalEstimatedUsd;

  // Work item 17433: this used to be total / points.length, where points is however many buckets
  // the server returned - days that HAD spend. A window with spend on 3 of 30 days reported a
  // daily average ten times too high, and the projection carried the error into a monthly figure.
  // The divisor is the window itself. data.window is the rolling window the request asked for
  // ("7" | "30" | "90"); falling back to the bucket count keeps the old behaviour if it is ever
  // absent, rather than dividing by zero.
  const windowDays = Number.parseInt(data.window, 10);
  const divisor = Number.isFinite(windowDays) && windowDays > 0 ? windowDays : points.length;
  const avgDaily = divisor > 0 ? total / divisor : 0;
  const projectedMonthly = avgDaily * 30;

  return (
    <div className="flex flex-col gap-6">
      {/* KPI tiles */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Spend', value: fmtUsd(total) },
          { label: 'Avg Daily', value: fmtUsd(avgDaily) },
          { label: 'Projected Monthly', value: fmtUsd(projectedMonthly) },
          {
            label: 'Judge Tokens (platform overhead)',
            value: fmtJudgeTokens(data.totalJudgeTokens ?? 0),
          },
        ].map((kpi) => (
          <div key={kpi.label} className="p-4 rounded-xl border border-zinc-800 bg-zinc-900">
            <p className="text-xs text-zinc-400">{kpi.label}</p>
            <p className="text-xl font-bold text-zinc-100 mt-1">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Line chart — user-billed spend */}
      <LineChart points={points} width={600} height={140} valueFormatter={fmtUsd} />

      {/* Judge-token overhead series — distinct from user-billed spend */}
      {hasJudgeOverhead && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-zinc-400">
            Quality gate judge tokens per bucket
            <span className="text-zinc-600"> — platform overhead, not billed to user credits</span>
          </p>
          <LineChart
            points={judgePoints}
            width={600}
            height={100}
            color="#f59e0b"
            valueFormatter={(v) => `${v.toLocaleString()} tokens`}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic sortable table shared by provider + outputType sub-views
//
// US-24-GR-26 item 3: these rows are bounded in-memory aggregates (one row
// per provider/model or per output type), so a full client-side sort is
// safe and cheap — this is real sorting, not a page-scoped approximation.
// ---------------------------------------------------------------------------

type SortableColumnKey =
  | 'key'
  | 'runs'
  | 'inputTokens'
  | 'outputTokens'
  | 'estimatedUsd'
  | 'creditsCharged'
  | 'judgeTokens';

type SortDirection = 'asc' | 'desc';

interface SortableColumn {
  key: SortableColumnKey;
  label: ReactNode;
  title?: string;
}

const SORTABLE_COLUMNS: SortableColumn[] = [
  { key: 'key', label: 'Key' },
  { key: 'runs', label: 'Runs' },
  { key: 'inputTokens', label: 'Input Tokens' },
  { key: 'outputTokens', label: 'Output Tokens' },
  { key: 'estimatedUsd', label: <AbbrLabel term="Est. USD" /> },
  { key: 'creditsCharged', label: 'Credits' },
  {
    key: 'judgeTokens',
    label: 'Judge Tokens',
    title: 'Quality gate judge tokens — platform overhead, not billed to users',
  },
];

function compareRows(a: CostRow, b: CostRow, key: SortableColumnKey): number {
  if (key === 'key') return a.key.localeCompare(b.key);
  const av = a[key] ?? -Infinity;
  const bv = b[key] ?? -Infinity;
  return av - bv;
}

function SortableTable({ rows }: { rows: CostRow[] }) {
  const [sortKey, setSortKey] = useState<SortableColumnKey>('estimatedUsd');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  function handleSort(key: SortableColumnKey) {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sortedRows = [...rows].sort((a, b) => {
    const cmp = compareRows(a, b, sortKey);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs text-left border-collapse">
        <thead>
          <tr className="border-b border-zinc-800">
            {SORTABLE_COLUMNS.map((col) => {
              const isActive = sortKey === col.key;
              const ariaSort = isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <th key={col.key} className="px-3 py-2 text-zinc-400 font-medium" aria-sort={ariaSort}>
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    title={col.title}
                    className={[
                      'flex items-center gap-1 hover:text-zinc-200 transition-colors',
                      isActive ? 'text-violet-300' : '',
                    ].join(' ')}
                    aria-label={`Sort by ${col.label} ${isActive && sortDir === 'asc' ? 'descending' : 'ascending'}`}
                  >
                    {col.label}
                    {col.key === 'judgeTokens' && <span className="text-zinc-600">(overhead)</span>}
                    <span aria-hidden="true" className="text-zinc-600">
                      {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.key} className="border-b border-zinc-800/50 hover:bg-zinc-800/40">
              <td className="px-3 py-2 text-zinc-300 font-medium">{row.key}</td>
              <td className="px-3 py-2 text-zinc-300">{row.runs.toLocaleString()}</td>
              <td className="px-3 py-2 text-zinc-300">{row.inputTokens.toLocaleString()}</td>
              <td className="px-3 py-2 text-zinc-300">{row.outputTokens.toLocaleString()}</td>
              <td className="px-3 py-2 text-zinc-300">{fmtUsd(row.estimatedUsd)}</td>
              <td className="px-3 py-2 text-zinc-300">{fmtCredits(row.creditsCharged)}</td>
              <td className="px-3 py-2 text-zinc-500">{fmtJudgeTokens(row.judgeTokens)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

interface AdjustTarget {
  profileId: string;
  creditsCharged: number | null;
}

export function CostAnalysisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = (searchParams.get('view') as SubView | null) ?? 'provider';
  const deepLinkProfileId = searchParams.get('profileId');
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadStoredWindow);
  const [data, setData] = useState<CostResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Adjust Credits modal state
  const [adjustTarget, setAdjustTarget] = useState<AdjustTarget | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  function handleWindowChange(w: TimeWindow) {
    setTimeWindow(w);
    saveStoredWindow(w);
  }

  const setView = (v: SubView) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', v);
      return next;
    });
  };

  function handleExportCsv() {
    if (!data) return;
    downloadCsv(`cost-${activeView}`, `window${timeWindow}`, COST_CSV_COLUMNS, data.rows);
  }

  const beginLoad = useLatestRequest();

  const fetchCost = useCallback(async () => {
    const isCurrent = beginLoad();
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCost({ window: timeWindow, groupBy: SUBVIEW_GROUPBY[activeView] });
      if (!isCurrent()) return;
      setData(result);
    } catch (err: unknown) {
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : 'Failed to load cost data');
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [beginLoad, timeWindow, activeView]);

  useEffect(() => {
    startTransition(() => { void fetchCost(); });
  }, [fetchCost]);

  async function handleAdjustConfirm(req: Parameters<typeof adjustCredits>[0]) {
    try {
      const result = await adjustCredits(req);
      setAdjustTarget(null);
      setToast({
        variant: 'success',
        message: `Adjusted: ${req.amount > 0 ? '+' : ''}${req.amount} credits → new balance ${result.newBalance.toLocaleString()} — ${result.affectedBalance}`,
        durationMs: 6000,
      });
      // Refresh cost data to reflect any ledger changes on next poll
      void fetchCost();
    } catch (err) {
      if (err instanceof InsufficientBalanceError) {
        setToast({ variant: 'error', message: `Insufficient balance: ${err.message}` });
      } else if (err instanceof ProfileNotFoundError) {
        setToast({ variant: 'error', message: `Profile not found: ${req.profileId}` });
      } else if (err instanceof CreditCeilingError) {
        // Work item 17267: show the ratified ceiling reason verbatim. It names which ceiling
        // refused and the figure that breached it, so it is the message the operator needs -
        // not a generic failure. Longer dwell because the sentence carries real numbers.
        setToast({ variant: 'error', message: err.message, durationMs: 12000 });
      } else {
        setToast({
          variant: 'error',
          message: err instanceof Error ? err.message : 'Failed to adjust credits',
        });
      }
      // Re-throw so the modal spinner stops
      throw err;
    }
  }

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-sm font-semibold text-zinc-100">Cost Analysis</h1>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={isLoading || !data || data.rows.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors disabled:opacity-50 min-h-[48px]"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <TimeWindowSelector value={timeWindow} onChange={handleWindowChange} />
        </div>
      </div>

      {/* Sub-view tabs */}
      <div
        role="tablist"
        aria-label="Cost sub-views"
        className="flex gap-1 mb-6 border-b border-zinc-800 pb-0"
      >
        {(Object.keys(SUBVIEW_LABELS) as SubView[]).map((view) => (
          <button
            key={view}
            type="button"
            role="tab"
            aria-selected={activeView === view}
            onClick={() => setView(view)}
            className={[
              'px-4 py-2 text-xs font-medium border-b-2 transition-colors -mb-px min-h-[48px]',
              activeView === view
                ? 'border-violet-500 text-violet-300'
                : 'border-transparent text-zinc-400 hover:text-zinc-200',
            ].join(' ')}
          >
            {SUBVIEW_LABELS[view]}
          </button>
        ))}
      </div>

      {/* Ledger-unavailable alert: a failed ledger read is not an empty ledger */}
      {data?._ledgerUnavailable && (
        <div
          role="alert"
          className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300"
        >
          Credit ledger unavailable — credits charged could not be read for this window. Numbers
          are based on token-count estimates only.
        </div>
      )}

      {/* Ledger-empty banner */}
      {data?._ledgerEmpty && !data._ledgerUnavailable && (
        <div
          role="alert"
          className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-xs text-yellow-300"
        >
          Credit ledger has no entries for this window. Numbers are based on token-count estimates
          only. Real deductions require US-21-GR-08.
        </div>
      )}

      {/* Unpriced models badge */}
      {data?._unpricedModels?.length ? (
        <div className="mb-4 flex items-center gap-2">
          <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
            Unpriced models: {data._unpricedModels.join(', ')}
          </span>
        </div>
      ) : null}

      {/* Judge-token overhead badge — WS-3 item 2.1 (platform overhead, not user credits) */}
      {data?.totalJudgeTokens ? (
        <div className="mb-4 flex items-center gap-2">
          <span className="px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs">
            Quality gate judge overhead: {data.totalJudgeTokens.toLocaleString()} tokens
            <span className="text-zinc-500"> (platform cost — not billed to user credits)</span>
          </span>
        </div>
      ) : null}

      {/* Error */}
      {error && (
        <div
          role="alert"
          className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300"
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-8 bg-zinc-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && data && data.rows.length === 0 && (
        <div className="flex items-center justify-center h-40 text-zinc-500 text-sm">
          No agent runs in this window.
        </div>
      )}

      {/* Sub-view content */}
      {!isLoading && !error && data && data.rows.length > 0 && (
        <div role="tabpanel" aria-label={SUBVIEW_LABELS[activeView]}>
          {activeView === 'provider'    && <ProviderView data={data} />}
          {activeView === 'user'        && (
            <UserView
              data={data}
              deepLinkProfileId={deepLinkProfileId}
              onAdjustCredits={(target) =>
                setAdjustTarget({ profileId: target.profileId, creditsCharged: target.creditsCharged })
              }
            />
          )}
          {activeView === 'outputType'  && <OutputTypeView data={data} />}
          {activeView === 'time'        && <TimeView data={data} />}
        </div>
      )}
      {/* Adjust Credits modal */}
      <AdjustCreditsModal
        isOpen={adjustTarget !== null}
        profileId={adjustTarget?.profileId ?? ''}
        currentBalance={adjustTarget?.creditsCharged ?? undefined}
        onConfirm={handleAdjustConfirm}
        onCancel={() => setAdjustTarget(null)}
      />

      {/* Toast notifications */}
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </main>
  );
}
